import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { downloadCsv } from '../DateRangeFilter';
import { printHtmlSilently } from '../../lib/silentPrint';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { TrendingUp, TrendingDown, ShoppingBag, Receipt, Wallet, Clock, RefreshCw, FileText, FileSpreadsheet, PieChart as PieIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
const CHART_COLORS = ['#7c3aed', '#2563eb', '#059669', '#d97706', '#dc2626', '#0891b2', '#db2777', '#65a30d', '#4f46e5', '#ea580c', '#0d9488', '#9333ea', '#64748b'];

const todayISO = () => new Date().toISOString().substring(0, 10);

const periodFor = (preset) => {
  const now = new Date();
  const end = todayISO();
  if (preset === 'today') return { start: end, end };
  if (preset === 'week') {
    const d = new Date(now);
    const dow = (d.getDay() + 6) % 7; // segunda = 0
    d.setDate(d.getDate() - dow);
    return { start: d.toISOString().substring(0, 10), end };
  }
  if (preset === 'month') {
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: d.toISOString().substring(0, 10), end };
  }
  return { start: end, end };
};

const presetLabel = { today: 'Hoje', week: 'Esta Semana', month: 'Este Mês', custom: 'Personalizado' };

export const FinancialSummary = () => {
  const [preset, setPreset] = useState('month');
  const [range, setRange] = useState(periodFor('month'));
  const [data, setData] = useState(null);
  const [byCategory, setByCategory] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (r) => {
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        axios.get(`${API}/financial/summary`, { params: { start_date: r.start, end_date: r.end }, withCredentials: true }),
        axios.get(`${API}/financial/expenses/by-category`, { params: { start_date: r.start, end_date: r.end, mode: 'paid' }, withCredentials: true }),
      ]);
      setData(sRes.data);
      setByCategory(cRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(range); }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (p) => {
    setPreset(p);
    if (p !== 'custom') {
      const r = periodFor(p);
      setRange(r);
      load(r);
    }
  };

  const applyCustom = () => { setPreset('custom'); load(range); };

  const exportExcel = () => {
    if (!data) return;
    const rows = [
      { item: 'Período', valor: `${range.start} a ${range.end} (${presetLabel[preset]})` },
      { item: 'Receita de Vendas Líquida', valor: data.sales_revenue },
      { item: '   Vendas à vista', valor: data.sales_cash },
      { item: '   Recebimentos de crediário', valor: data.crediario_received },
      { item: 'Compras Pagas no Período', valor: data.purchases_paid },
      { item: 'Despesas Pagas no Período', valor: data.expenses_paid },
      { item: 'Compras Pendentes', valor: data.purchases_pending },
      { item: 'Despesas / Contas a Pagar Pendentes', valor: data.expenses_pending },
      { item: 'RESULTADO DE CAIXA DO PERÍODO', valor: data.cash_result },
      { item: '', valor: '' },
      { item: 'DESPESAS PAGAS POR CATEGORIA', valor: '' },
      ...(byCategory.items || []).map((c) => ({ item: c.category, valor: c.total })),
    ];
    downloadCsv(`gestao_financeira_${range.start}_a_${range.end}.csv`, rows, [
      { key: 'item', label: 'Item' },
      { key: 'valor', label: 'Valor (R$)', format: (v) => (typeof v === 'number' ? v.toFixed(2).replace('.', ',') : v) },
    ]);
  };

  const exportPdf = () => {
    if (!data) return;
    const catRows = (byCategory.items || []).map((c) =>
      `<tr><td>${c.category}</td><td style="text-align:right">${fmt(c.total)}</td><td style="text-align:right">${c.count}</td></tr>`).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Gestão Financeira</title>
      <style>
        body{font-family:Arial,Helvetica,sans-serif;color:#0f172a;padding:32px;max-width:800px;margin:0 auto}
        h1{font-size:22px;margin:0 0 4px} .sub{color:#64748b;font-size:12px;margin-bottom:20px}
        .result{border:2px solid ${data.cash_result >= 0 ? '#059669' : '#dc2626'};border-radius:10px;padding:16px;margin:16px 0;background:${data.cash_result >= 0 ? '#ecfdf5' : '#fef2f2'}}
        .result .lbl{font-size:11px;letter-spacing:2px;text-transform:uppercase;color:#64748b;font-weight:bold}
        .result .val{font-size:34px;font-weight:900;color:${data.cash_result >= 0 ? '#059669' : '#dc2626'}}
        table{width:100%;border-collapse:collapse;margin:8px 0 20px} td,th{padding:8px 10px;border-bottom:1px solid #e2e8f0;font-size:13px}
        th{text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:1px;color:#64748b}
        .strong td{font-weight:bold} .foot{color:#94a3b8;font-size:10px;margin-top:24px;text-align:center}
      </style></head><body>
      <h1>Gestão Financeira — Resumo de Caixa</h1>
      <div class="sub">Período: ${range.start} a ${range.end} (${presetLabel[preset]}) · Regime de caixa · Emitido em ${new Date().toLocaleString('pt-BR')}</div>
      <div class="result"><div class="lbl">Resultado de Caixa do Período</div><div class="val">${fmt(data.cash_result)}</div>
        <div style="font-size:11px;color:#64748b;margin-top:4px">Receita de Vendas Líquida − Compras Pagas − Despesas Pagas</div></div>
      <table>
        <tr><th>Item</th><th style="text-align:right">Valor</th></tr>
        <tr><td>Receita de Vendas Líquida</td><td style="text-align:right">${fmt(data.sales_revenue)}</td></tr>
        <tr><td style="padding-left:24px;color:#64748b">Vendas à vista</td><td style="text-align:right;color:#64748b">${fmt(data.sales_cash)}</td></tr>
        <tr><td style="padding-left:24px;color:#64748b">Recebimentos de crediário</td><td style="text-align:right;color:#64748b">${fmt(data.crediario_received)}</td></tr>
        <tr><td>Compras Pagas no Período</td><td style="text-align:right">${fmt(data.purchases_paid)}</td></tr>
        <tr><td>Despesas Pagas no Período</td><td style="text-align:right">${fmt(data.expenses_paid)}</td></tr>
        <tr><td>Compras Pendentes</td><td style="text-align:right">${fmt(data.purchases_pending)}</td></tr>
        <tr><td>Despesas / Contas a Pagar Pendentes</td><td style="text-align:right">${fmt(data.expenses_pending)}</td></tr>
        <tr class="strong"><td>RESULTADO DE CAIXA DO PERÍODO</td><td style="text-align:right">${fmt(data.cash_result)}</td></tr>
      </table>
      ${catRows ? `<h3 style="font-size:14px">Despesas Pagas por Categoria</h3>
      <table><tr><th>Categoria</th><th style="text-align:right">Total</th><th style="text-align:right">Qtd</th></tr>${catRows}</table>` : ''}
      <div class="foot">Smart Flow — Gestão Financeira</div>
      </body></html>`;
    printHtmlSilently(html);
  };

  const presetBtn = (id, label) => (
    <button
      key={id}
      onClick={() => applyPreset(id)}
      data-testid={`fin-preset-${id}`}
      className={`px-4 py-2 rounded-md text-sm font-bold transition-colors ${preset === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
    >{label}</button>
  );

  const result = data?.cash_result ?? 0;
  const positive = result >= 0;
  const chartData = (byCategory.items || []).map((c) => ({ name: c.category, value: c.total }));

  return (
    <div data-testid="financial-summary">
      {/* Filtros + exportação */}
      <div className="bg-white rounded-md border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-wrap gap-2">
            {presetBtn('today', 'Hoje')}
            {presetBtn('week', 'Esta Semana')}
            {presetBtn('month', 'Este Mês')}
          </div>
          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">De</label>
              <Input type="date" value={range.start} data-testid="fin-start-date"
                onChange={(e) => setRange({ ...range, start: e.target.value })} className="h-9 w-40" />
            </div>
            <div>
              <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Até</label>
              <Input type="date" value={range.end} data-testid="fin-end-date"
                onChange={(e) => setRange({ ...range, end: e.target.value })} className="h-9 w-40" />
            </div>
            <Button onClick={applyCustom} variant="outline" className="h-9" data-testid="fin-apply-custom">Aplicar</Button>
          </div>
          <div className="flex items-end gap-2 ml-auto">
            <Button onClick={exportExcel} variant="outline" className="h-9 text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="fin-export-excel">
              <FileSpreadsheet size={16} className="mr-2" /> Excel
            </Button>
            <Button onClick={exportPdf} variant="outline" className="h-9 text-red-700 border-red-200 hover:bg-red-50" data-testid="fin-export-pdf">
              <FileText size={16} className="mr-2" /> PDF
            </Button>
            <Button onClick={() => load(range)} variant="ghost" className="h-9" data-testid="fin-refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* Resultado de Caixa (indicador principal) */}
      <div className={`rounded-xl border-2 p-6 mb-6 ${positive ? 'bg-emerald-50 border-emerald-300' : 'bg-red-50 border-red-300'}`} data-testid="cash-result-card">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Resultado de Caixa do Período</p>
            <p className={`text-5xl font-black mt-2 tabular-nums ${positive ? 'text-emerald-600' : 'text-red-600'}`} data-testid="cash-result-value">
              {fmt(result)}
            </p>
            <p className="text-xs text-slate-500 mt-2">Receita de Vendas Líquida − Compras Pagas − Despesas Pagas</p>
          </div>
          {positive
            ? <TrendingUp size={72} className="text-emerald-400" />
            : <TrendingDown size={72} className="text-red-400" />}
        </div>
      </div>

      {/* Cards separados */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard icon={<Wallet className="text-emerald-600" size={20} />} label="Receita de Vendas Líquida"
          value={data?.sales_revenue} accent="text-emerald-600" testid="card-sales-revenue"
          sub={`À vista ${fmt(data?.sales_cash)} · Crediário ${fmt(data?.crediario_received)}`} />
        <SummaryCard icon={<ShoppingBag className="text-blue-600" size={20} />} label="Compras Pagas no Período"
          value={data?.purchases_paid} accent="text-blue-600" testid="card-purchases-paid" />
        <SummaryCard icon={<Receipt className="text-purple-600" size={20} />} label="Despesas Pagas no Período"
          value={data?.expenses_paid} accent="text-purple-600" testid="card-expenses-paid" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <SummaryCard icon={<Clock className="text-amber-600" size={20} />} label="Compras Pendentes"
          value={data?.purchases_pending} accent="text-amber-600" testid="card-purchases-pending"
          sub="Notas de compra ainda não pagas" />
        <SummaryCard icon={<Clock className="text-amber-600" size={20} />} label="Despesas / Contas a Pagar Pendentes"
          value={data?.expenses_pending} accent="text-amber-600" testid="card-expenses-pending"
          sub="Despesas fixas ainda não pagas" />
      </div>

      {/* Gráfico: Despesas pagas por categoria */}
      <div className="bg-white rounded-md border border-slate-200 p-4 mt-6" data-testid="expenses-by-category-chart">
        <div className="flex items-center gap-2 mb-3">
          <PieIcon size={18} className="text-purple-600" />
          <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Despesas Pagas por Categoria</h3>
          <span className="ml-auto text-sm font-bold text-purple-600">{fmt(byCategory.total)}</span>
        </div>
        {chartData.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center" data-testid="chart-empty">Nenhuma despesa paga no período selecionado</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={chartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${((e.percent || 0) * 100).toFixed(0)}%`}>
                    {chartData.map((entry, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-1.5" data-testid="category-breakdown-list">
              {(byCategory.items || []).map((c, i) => {
                const pct = byCategory.total > 0 ? (c.total / byCategory.total) * 100 : 0;
                return (
                  <div key={c.category} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                    <span className="text-sm text-slate-700 flex-1 truncate">{c.category}</span>
                    <span className="text-xs text-slate-400">{pct.toFixed(0)}%</span>
                    <span className="text-sm font-bold text-slate-900 tabular-nums w-24 text-right">{fmt(c.total)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, accent, sub, testid }) => (
  <div className="bg-white rounded-md border border-slate-200 p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-500">{label}</p>
    </div>
    <p className={`text-3xl font-black tabular-nums ${accent}`}>{fmt(value)}</p>
    {sub && <p className="text-[11px] text-slate-400 mt-1">{sub}</p>}
  </div>
);
